<script setup>
import { nextTick, onMounted, onUnmounted, ref } from "vue";

import { ButtonSize, Severity, TuButton } from "@tinyurldev/tu-ui-vue";

import POPOVER_SIZE from "@Constants/popover-size";

const emits = defineEmits(["close"]);

const props = defineProps({
  size: {
    type: String,
    default: POPOVER_SIZE.SMALL,
  },
});

const popoverRef = ref(null);

function closePopover() {
  emits("close");
}

onMounted(() => {
  Bus.$on("closePopover2", closePopover);

  nextTick(() => {
    if (popoverRef.value && popoverRef.value.focus) {
      popoverRef.value.focus();
    }
  });
});

onUnmounted(() => {
  Bus.$off("closePopover2");
});
</script>

<template>
  <FocusTrap>
    <div
      class="pop-overhauling"
      :class="[`pop-overhauling--${size}`]"
    >
      <!-- Backdrop -->
      <div
        class="pop-overhauling__backdrop"
        @click="closePopover"
      >
        <span class="sr-only">Popover Backdrop</span>
      </div>

      <!-- Slider -->
      <div class="pop-overhauling__content">
        <!-- Header -->
        <div class="pop-overhauling__header">
          <template v-if="$slots.title">
            <h5 class="pop-overhauling__title">
              <slot name="title" />
            </h5>
          </template>
          <TuButton
            class="pop-overhauling__close-button _close_popover"
            aria-label="Close"
            label=""
            :severity="Severity.CANCEL"
            :size="ButtonSize.SMALL"
            @click="closePopover"
          >
            <i class="pop-overhauling__close-button__icon fa-solid fa-xmark" />
          </TuButton>
        </div>

        <!-- Content -->
        <div
          ref="popoverRef"
          class="pop-overhauling__body"
          tabindex="-1"
        >
          <slot>
            <div class="h-100 w-100 flex-center-center">
              <div class="text-center">
                <svg
                  width="135"
                  height="175"
                  viewBox="0 0 128 128"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M107.52 109.518C106.043 111.057 104.269 112.281 102.306 113.117C100.343 113.952 98.2312 114.381 96.0977 114.378H18.5977C16.4647 114.381 14.353 113.952 12.3903 113.117C10.4276 112.282 8.65438 111.057 7.17773 109.518L39.0627 74.543L44.6977 80.7205C46.3014 82.48 48.2545 83.8855 50.4323 84.8472C52.6101 85.8089 54.9646 86.3057 57.3452 86.3057C59.7259 86.3057 62.0804 85.8089 64.2582 84.8472C66.4359 83.8855 68.3891 82.48 69.9927 80.7205L75.6277 74.543L107.52 109.518Z"
                    fill="#087DA8"
                  />
                  <path
                    d="M89.8722 58.922L69.9997 80.722C68.3958 82.4816 66.4424 83.8872 64.2645 84.8489C62.0865 85.8107 59.7318 86.3074 57.3509 86.3074C54.9701 86.3074 52.6154 85.8107 50.4374 84.8489C48.2594 83.8872 46.3061 82.4816 44.7022 80.722L7.17969 39.567C8.65642 38.0278 10.4296 36.8037 12.3923 35.9684C14.3551 35.1331 16.4666 34.704 18.5997 34.707H80.6397C79.5195 39.2031 79.804 43.9344 81.4547 48.2639C83.1054 52.5933 86.0431 56.313 89.8722 58.922Z"
                    fill="#087DA8"
                  />
                  <path
                    d="M96.0975 115.88H18.6C14.0003 115.874 9.59074 114.044 6.33827 110.791C3.08581 107.539 1.25595 103.129 1.25 98.5295V77.977C1.25 77.5792 1.40804 77.1977 1.68934 76.9164C1.97064 76.6351 2.35218 76.477 2.75 76.477C3.14782 76.477 3.52936 76.6351 3.81066 76.9164C4.09196 77.1977 4.25 77.5792 4.25 77.977V98.5295C4.25463 102.334 5.76799 105.981 8.45813 108.671C11.1483 111.362 14.7956 112.875 18.6 112.88H96.1C99.9044 112.875 103.552 111.362 106.242 108.671C108.932 105.981 110.445 102.334 110.45 98.5295V63.0295C106.814 64.2727 102.934 64.6309 99.1325 64.0743C95.3307 63.5177 91.7163 62.0625 88.5895 59.8293C85.4627 57.5961 82.9135 54.6494 81.1535 51.2338C79.3936 47.8181 78.4736 44.0319 78.47 40.1895C78.4703 38.8494 78.5807 37.5116 78.8 36.1895H18.6C14.7925 36.1942 11.1426 37.7099 8.45195 40.4039C5.76131 43.0978 4.25 46.7496 4.25 50.557V66.277C4.25 66.6749 4.09196 67.0564 3.81066 67.3377C3.52936 67.619 3.14782 67.777 2.75 67.777C2.35218 67.777 1.97064 67.619 1.68934 67.3377C1.40804 67.0564 1.25 66.6749 1.25 66.277V50.557C1.25529 45.9572 3.08493 41.5472 6.33754 38.2946C9.59015 35.042 14.0001 33.2123 18.6 33.207H80.64C80.868 33.2067 81.0931 33.2584 81.2981 33.3582C81.5031 33.4579 81.6827 33.6031 81.8232 33.7826C81.9638 33.9622 82.0615 34.1714 82.109 34.3944C82.1565 34.6174 82.1526 34.8483 82.0975 35.0695C81.1342 38.9251 81.2769 42.974 82.509 46.7522C83.7412 50.5304 86.013 53.885 89.064 56.4314C92.1151 58.9778 95.8219 60.6129 99.7596 61.1494C103.697 61.6858 107.706 61.1019 111.327 59.4645C111.556 59.3608 111.807 59.316 112.057 59.3344C112.308 59.3528 112.55 59.4338 112.761 59.5699C112.972 59.706 113.145 59.8928 113.265 60.1134C113.385 60.3339 113.448 60.581 113.448 60.832V98.5295C113.442 103.129 111.613 107.539 108.36 110.792C105.107 114.045 100.697 115.874 96.0975 115.88Z"
                    fill="#343A40"
                  />
                  <path
                    d="M2.75 74.75C2.35218 74.75 1.97064 74.592 1.68934 74.3107C1.40804 74.0294 1.25 73.6478 1.25 73.25V71C1.25 70.6022 1.40804 70.2206 1.68934 69.9393C1.97064 69.658 2.35218 69.5 2.75 69.5C3.14782 69.5 3.52936 69.658 3.81066 69.9393C4.09196 70.2206 4.25 70.6022 4.25 71V73.25C4.25 73.6478 4.09196 74.0294 3.81066 74.3107C3.52936 74.592 3.14782 74.75 2.75 74.75Z"
                    fill="#343A40"
                  />
                  <path
                    d="M96.0972 115.877H18.5972C16.2616 115.885 13.9488 115.418 11.7998 114.503C9.65074 113.589 7.71045 112.246 6.09718 110.557C5.83365 110.282 5.68444 109.918 5.6798 109.537C5.67516 109.156 5.81543 108.788 6.07218 108.507L37.9572 73.5322C38.0978 73.378 38.269 73.2547 38.4599 73.1704C38.6508 73.0861 38.8572 73.0425 39.0659 73.0425C39.2746 73.0425 39.4811 73.0861 39.672 73.1704C39.8629 73.2547 40.0341 73.378 40.1747 73.5322L45.8072 79.7097C47.2701 81.3157 49.0521 82.5987 51.0392 83.4765C53.0263 84.3544 55.1748 84.8078 57.3472 84.8078C59.5196 84.8078 61.668 84.3544 63.6552 83.4765C65.6423 82.5987 67.4243 81.3157 68.8872 79.7097L74.5197 73.5322C74.6595 73.3759 74.8305 73.2507 75.0217 73.1646C75.2129 73.0784 75.42 73.0334 75.6297 73.0322C75.8389 73.0337 76.0456 73.0789 76.2363 73.1651C76.4271 73.2512 76.5977 73.3762 76.7372 73.5322L108.627 108.5C108.884 108.781 109.023 109.149 109.018 109.53C109.013 109.911 108.864 110.275 108.6 110.55C106.987 112.24 105.046 113.585 102.897 114.5C100.747 115.416 98.4337 115.885 96.0972 115.877ZM9.28218 109.44C11.8732 111.671 15.1828 112.892 18.6022 112.877H96.1022C99.5221 112.891 102.832 111.67 105.425 109.44L75.6322 76.7697L71.1047 81.7322C69.3608 83.6443 67.2372 85.1717 64.8696 86.2167C62.5021 87.2617 59.9426 87.8015 57.3547 87.8015C54.7667 87.8015 52.2073 87.2617 49.8397 86.2167C47.4722 85.1717 45.3486 83.6443 43.6047 81.7322L39.0797 76.7722L9.28218 109.44Z"
                    fill="#343A40"
                  />
                  <path
                    d="M57.3504 87.8072C54.7626 87.8032 52.2038 87.2611 49.8367 86.2153C47.4696 85.1695 45.3459 83.6428 43.6004 81.7322L6.07292 40.5772C5.8165 40.2958 5.67667 39.9274 5.68178 39.5467C5.68688 39.1661 5.83654 38.8016 6.10042 38.5272C7.71315 36.8376 9.65333 35.4944 11.8025 34.5798C13.9517 33.6651 16.2647 33.198 18.6004 33.2072H80.6404C80.8684 33.2069 81.0935 33.2585 81.2985 33.3583C81.5036 33.458 81.6832 33.6032 81.8237 33.7827C81.9642 33.9623 82.0619 34.1715 82.1094 34.3945C82.157 34.6175 82.153 34.8484 82.0979 35.0697C81.05 39.268 81.3145 43.6867 82.8558 47.73C84.3971 51.7733 87.1412 55.2468 90.7179 57.6822C90.8955 57.803 91.045 57.9606 91.1564 58.1443C91.2678 58.3279 91.3385 58.5334 91.3636 58.7467C91.3887 58.9601 91.3677 59.1763 91.3021 59.3808C91.2364 59.5853 91.1275 59.7733 90.9829 59.9322L71.1079 81.7322C69.3616 83.6437 67.2367 85.1709 64.8682 86.2168C62.4997 87.2627 59.9395 87.8043 57.3504 87.8072ZM9.28042 39.6422L45.8104 79.7097C47.2733 81.3156 49.0553 82.5986 51.0424 83.4765C53.0296 84.3543 55.178 84.8077 57.3504 84.8077C59.5228 84.8077 61.6713 84.3543 63.6584 83.4765C65.6455 82.5986 67.4275 81.3156 68.8904 79.7097L87.6404 59.1422C84.7801 56.883 82.4686 54.0053 80.8795 50.725C79.2905 47.4447 78.4651 43.8471 78.4654 40.2022C78.4656 38.862 78.576 37.5242 78.7954 36.2022H18.6004C15.181 36.1899 11.8719 37.4113 9.28042 39.6422Z"
                    fill="#343A40"
                  />
                  <path
                    d="M102.611 64.3425C97.8363 64.3425 93.169 62.9267 89.1992 60.2742C85.2294 57.6217 82.1354 53.8515 80.3083 49.4405C78.4812 45.0295 78.0031 40.1757 78.9346 35.493C79.866 30.8103 82.1651 26.509 85.5412 23.133C88.9172 19.7569 93.2185 17.4578 97.9012 16.5264C102.584 15.5949 107.438 16.073 111.849 17.9001C116.26 19.7272 120.03 22.8212 122.682 26.791C125.335 30.7608 126.751 35.4281 126.751 40.2025C126.743 46.6024 124.197 52.7379 119.672 57.2633C115.146 61.7887 109.011 64.3346 102.611 64.3425ZM102.611 19.0625C98.4296 19.0625 94.3424 20.3024 90.866 22.6252C87.3895 24.9481 84.6799 28.2498 83.0799 32.1126C81.4799 35.9754 81.0612 40.226 81.8769 44.3267C82.6926 48.4275 84.706 52.1943 87.6625 55.1507C90.619 58.1072 94.3857 60.1206 98.4865 60.9363C102.587 61.752 106.838 61.3334 110.701 59.7333C114.563 58.1333 117.865 55.4237 120.188 51.9473C122.511 48.4708 123.751 44.3836 123.751 40.2025C123.744 34.5979 121.515 29.2247 117.552 25.2616C113.589 21.2985 108.215 19.0691 102.611 19.0625Z"
                    fill="#343A40"
                  />
                  <path
                    d="M102.609 49.5154C102.212 49.5154 101.83 49.3574 101.549 49.0761C101.267 48.7947 101.109 48.4132 101.109 48.0154V25.0879C101.109 24.6901 101.267 24.3085 101.549 24.0272C101.83 23.7459 102.212 23.5879 102.609 23.5879C103.007 23.5879 103.389 23.7459 103.67 24.0272C103.951 24.3085 104.109 24.6901 104.109 25.0879V48.0154C104.109 48.4132 103.951 48.7947 103.67 49.0761C103.389 49.3574 103.007 49.5154 102.609 49.5154Z"
                    fill="#087DA8"
                  />
                  <path
                    d="M102.609 56.8175C102.212 56.8175 101.83 56.6595 101.549 56.3782C101.267 56.0969 101.109 55.7153 101.109 55.3175V53.75C101.109 53.3522 101.267 52.9706 101.549 52.6893C101.83 52.408 102.212 52.25 102.609 52.25C103.007 52.25 103.389 52.408 103.67 52.6893C103.951 52.9706 104.109 53.3522 104.109 53.75V55.32C104.109 55.7174 103.95 56.0983 103.669 56.379C103.388 56.6598 103.007 56.8175 102.609 56.8175Z"
                    fill="#087DA8"
                  />
                </svg>
                <br>
                <h4>Page not found</h4>
              </div>
            </div>
          </slot>
        </div>

        <!-- Footer -->
        <template v-if="$slots.footer">
          <div class="pop-overhauling__footer">
            <slot name="footer" />
          </div>
        </template>
      </div>
    </div>
  </FocusTrap>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.pop-overhauling {
  $self: &;

  height: 100%;
  width: 100%;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 15;

  &__backdrop {
    transition: all 0.5s cubic-bezier(0.77, 0, 0.175, 1);
    position: absolute;
    z-index: 1;
    background-color: rgba($color: color.$accent-dark, $alpha: 0.25);
    height: 100%;
    left: 0;
    top: 0;
    width: 100%;
  }

  &__content {
    transition: all 0.5s cubic-bezier(0.77, 0, 0.175, 1);
    position: relative;
    z-index: 2;
    background-color: color.$background-primary;
    height: 100%;
    box-shadow: 0 1rem 3rem rgba(33, 37, 41, 0.18);
    margin-left: auto;
    display: flex;
    flex-direction: column;
  }

  &__header {
    border-bottom: none;

    display: flex;
    align-items: center;
    justify-content: space-between;

    padding: spacing.$xxs;
  }

  &__title {
    font-weight: typography.$font-weight-bold;
    margin-bottom: 0;
  }

  &__close-button {
    display: flex;
    align-items: center;
    justify-content: center;

    width: spacing.$xxs;
    height: spacing.$xxs;

    &__icon {
      &::before {
        display: block;

        width: spacing.$xxxs;
        height: spacing.$xxxs;
      }
    }
  }

  &__body {
    flex-grow: 1;
    overflow-y: auto;

    &:focus {
      outline: none;
    }

    padding: 0 spacing.$xxs spacing.$xxs;
  }

  &--large {
    width: 100vw;

    @media (min-width: 1200px) {
      #{ $self }__content {
        width: 75vw;
        min-width: 1200px;
        max-width: 1400px;
      }
    }
  }
  &--medium {
    width: 100vw;

    @media (min-width: 768px) {
      #{ $self }__content {
        width: 650px;
      }
    }
  }
  &--small {
    width: 100vw;

    @media (min-width: 768px) {
      #{ $self }__content {
        width: 450px;
      }
    }
  }
}
</style>
